import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface RegistrationSuccessProps {
  userEmail: string;
  userName: string;
  onResendEmail?: () => void;
}

const RegistrationSuccess: React.FC<RegistrationSuccessProps> = ({
  userEmail,
  userName,
  onResendEmail,
}) => {
  const { resendVerificationEmail, loading } = useAuth();
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResendEmail = async () => {
    try {
      setResendError(null);
      await resendVerificationEmail();
      setResendSuccess(true);
      onResendEmail?.();
    } catch (error: any) {
      setResendError(error.message || 'Error al reenviar el email');
    }
  };

  return (
    <Card elevation={3} sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircleIcon 
          sx={{ 
            fontSize: 80, 
            color: 'success.main', 
            mb: 2 
          }} 
        />
        
        <Typography variant="h4" gutterBottom color="success.main">
          ¡Registro Exitoso!
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          ¡Hola {userName}!
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
          <Typography variant="body1" gutterBottom>
            <strong>Tu cuenta ha sido creada exitosamente.</strong>
          </Typography>
          <Typography variant="body2">
            Hemos enviado un email de verificación a:
          </Typography>
          <Chip 
            label={userEmail} 
            color="primary" 
            variant="outlined" 
            sx={{ mt: 1, fontWeight: 'bold' }}
          />
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            <EmailIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            <strong>Revisa tu bandeja de entrada</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Haz clic en el enlace del email para verificar tu cuenta y acceder a todas las funcionalidades de Facto.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            ¿No recibiste el email?
          </Typography>
          
          {resendSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Email de verificación reenviado exitosamente.
            </Alert>
          )}
          
          {resendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resendError}
            </Alert>
          )}
          
          <Button
            variant="outlined"
            onClick={handleResendEmail}
            disabled={loading}
            startIcon={loading ? undefined : <RefreshIcon />}
            sx={{ mr: 2 }}
          >
            {loading ? 'Enviando...' : 'Reenviar Email'}
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Una vez verificado tu email, podrás:
          </Typography>
          <Box component="ul" sx={{ textAlign: 'left', mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Subir facturas para factoring
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Explorar oportunidades de inversión
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Acceder a tu dashboard personalizado
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            ¿Tienes problemas? Contáctanos en{' '}
            <a href="mailto:soporte@facto.com.py" style={{ color: 'inherit' }}>
              soporte@facto.com.py
            </a>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RegistrationSuccess;
